// Steuerpaket S2: WEG-Ausgaben auf die Einheiten verteilen (Björn, Regel 5): nach dem Schlüssel aus der
// Hausgeldabrechnung – Standard MEA, je Kostenkonto abweichend „je Einheit“ oder fest vereinbarte Anteile.
// Rücklagenzuführungen sind keine Ausgaben der WEG und kommen hier nicht vor (berechneObjekt).
import type { Kategorie, ObjektErgebnis } from './steuerpaket';

export interface WegEinheit { einheit_id: string; bezeichnung: string; eigentuemer_id: string | null; mea: number | null }
export type SchluesselArt = 'mea' | 'einheit' | 'fest';
export interface Kostenschluessel { konto: string; art: SchluesselArt; /** nur 'fest': Anteile je einheit_id (beliebige Einheit, z. B. Verbrauch) */ anteile?: Record<string, number> | null }

export interface EinheitAnteil {
  einheit_id: string;
  bezeichnung: string;
  eigentuemer_id: string | null;
  posten: { kategorie: Kategorie; konto: string; betrag: number; schluessel: SchluesselArt }[];
  summen: Partial<Record<Kategorie, number>>;
  werbungskosten: number;
}
export interface WegVerteilung { einheiten: EinheitAnteil[]; hinweise: string[] }

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Teilt einen Betrag nach Gewichten auf; Rundungsrest geht an den größten Anteil, damit die Summe aufgeht. */
export function teile(betrag: number, gewichte: readonly number[]): number[] {
  const summe = gewichte.reduce((s, g) => s + g, 0);
  if (summe <= 0) return gewichte.map(() => 0);
  const teile = gewichte.map((g) => r2((betrag * g) / summe));
  const rest = r2(betrag - teile.reduce((s, t) => s + t, 0));
  if (rest !== 0) { const i = gewichte.indexOf(Math.max(...gewichte)); teile[i] = r2(teile[i] + rest); }
  return teile;
}

export function verteileWeg(ergebnis: ObjektErgebnis, einheiten: readonly WegEinheit[], schluessel: readonly Kostenschluessel[] = []): WegVerteilung {
  const hinweise: string[] = [];
  const ohneMea = einheiten.filter((e) => !(e.mea && e.mea > 0));
  if (ohneMea.length) hinweise.push(`Ohne MEA: ${ohneMea.map((e) => e.bezeichnung).join(', ')} – nach MEA verteilte Kosten gehen an diese Einheiten nicht.`);
  const nachKonto = new Map(schluessel.map((s) => [s.konto, s]));
  const anteile: EinheitAnteil[] = einheiten.map((e) => ({ einheit_id: e.einheit_id, bezeichnung: e.bezeichnung, eigentuemer_id: e.eigentuemer_id, posten: [], summen: {}, werbungskosten: 0 }));

  for (const p of ergebnis.posten) {
    const s = nachKonto.get(p.konto) ?? { konto: p.konto, art: 'mea' as const };
    let gewichte: number[];
    if (s.art === 'einheit') gewichte = einheiten.map(() => 1);
    else if (s.art === 'fest') {
      gewichte = einheiten.map((e) => Math.max(0, s.anteile?.[e.einheit_id] ?? 0));
      if (!gewichte.some((g) => g > 0)) { hinweise.push(`„${p.konto}“: feste Anteile fehlen – nach MEA verteilt.`); gewichte = einheiten.map((e) => e.mea ?? 0); }
    } else gewichte = einheiten.map((e) => e.mea ?? 0);
    const art: SchluesselArt = s.art === 'fest' && !(s.anteile && Object.values(s.anteile).some((v) => v > 0)) ? 'mea' : s.art;
    // Einnahmen der WEG (z. B. Zinsen) mindern die Kosten der Eigentümer → als negative Werbungskosten
    const kosten = ['miete', 'umlagen', 'stellplatz', 'sonstige_einnahmen'].includes(p.kategorie) ? -p.betrag : p.betrag;
    const kat: Kategorie = kosten === p.betrag ? p.kategorie : 'sonstige_wk';
    teile(kosten, gewichte).forEach((t, i) => {
      if (t === 0) return;
      anteile[i].posten.push({ kategorie: kat, konto: p.konto, betrag: t, schluessel: art });
    });
  }
  for (const a of anteile) {
    for (const p of a.posten) a.summen[p.kategorie] = r2((a.summen[p.kategorie] ?? 0) + p.betrag);
    a.werbungskosten = r2(a.posten.reduce((s, p) => s + p.betrag, 0));
  }
  return { einheiten: anteile, hinweise };
}
